// Charteris Phase 2.5 Task 5, fix round 1 (2026-10-07): the London 08:40
// decision clock shared by every gated-futures Signal tab (JapanSignalTab,
// CharterisSignalTab). Extracted verbatim from JapanSignalTab.vue so a fix to
// the DST correction, the 09:00 missed-publish cutoff or the UK-holiday
// banner reaches both funds at once.
//
// Owns: the 1 s London clock tick, the UK bank-holiday calendar
// (/api/calendar/holidays, current + previous London year, retried every
// 10 min while unavailable), and every derived state the verdict needs
// (noTrade / lateState / awaiting / countdown / decided-at / last-decision).
// The caller owns its signal polling and passes the signal ref in.
import { computed, onMounted, onUnmounted, ref, type Ref } from 'vue'
import { api, type GatedFuturesSignal } from '../api/client'
import { fmtDayMon } from '../components/japan/japanReasons'

export const DECISION_MINUTES = 8 * 60 + 40   // 08:40 London
export const MISSED_AFTER_MINUTES = 9 * 60    // 09:00 London: HL dealing cutoff
const TICK_MS = 1_000
const CALENDAR_RETRY_MS = 10 * 60_000

// --- pure London calendar helpers (exported for unit tests) -----------------
const londonDateFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
})
const londonTimeFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
})

/** YYYY-MM-DD in Europe/London. */
export function londonDate(d: Date): string {
  return londonDateFmt.format(d)
}
/** Seconds since London midnight. */
export function londonSecondsOfDay(d: Date): number {
  const [h, m, s] = londonTimeFmt.format(d).split(':').map(Number)
  return h * 3600 + m * 60 + s
}
/** HH:MM London wall-clock. */
export function londonHhmm(d: Date): string {
  return londonTimeFmt.format(d).slice(0, 5)
}
export function isWeekendIso(iso: string): boolean {
  const [y, m, d] = iso.split('-').map(Number)
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  return wd === 0 || wd === 6
}
export function addDaysIso(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}
/** The UTC instant of 08:40 London on calendar date `iso` (DST-aware). */
export function decisionInstant(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  const guess = Date.UTC(y, m - 1, d, 8, 40)
  // London is UTC+0 or UTC+1: correct the naive guess by London's offset.
  const offsetSecs = londonSecondsOfDay(new Date(guess)) - DECISION_MINUTES * 60
  return new Date(guess - offsetSecs * 1000)
}
export function fmtCountdown(diffMs: number): string {
  const days = Math.floor(diffMs / 86400000)
  const hrs = Math.floor((diffMs % 86400000) / 3600000)
  const mins = Math.floor((diffMs % 3600000) / 60000)
  const secs = Math.floor((diffMs % 60000) / 1000)
  if (days > 0) return `${days}d ${hrs}h ${mins}m`
  if (hrs > 0) return `${hrs}h ${mins}m ${secs}s`
  return `${mins}m ${secs}s`
}

export function useLondonDecisionClock(signal: Ref<GatedFuturesSignal | null>) {
  const now = ref(new Date())
  // UK bank holidays (YYYY-MM-DD) from /api/calendar/holidays (no fund = UK +
  // NYSE rows; only ukHoliday rows kept). null = not loaded / fetch failed.
  const ukHolidays = ref<Set<string> | null>(null)

  /** Mon-Fri and not a known UK bank holiday. */
  function isUkBusinessDay(iso: string): boolean {
    return !isWeekendIso(iso) && !(ukHolidays.value?.has(iso) ?? false)
  }
  /** UK business days strictly after `from` up to and including `to`. */
  function businessDaysBetween(from: string, to: string): number {
    let n = 0
    for (let d = addDaysIso(from, 1); d <= to; d = addDaysIso(d, 1)) {
      if (isUkBusinessDay(d)) n++
    }
    return n
  }

  const todayLondon = computed(() => londonDate(now.value))
  const nowSecs = computed(() => londonSecondsOfDay(now.value))
  const pastDecisionTime = computed(() => nowSecs.value >= DECISION_MINUTES * 60)
  const isWeekend = computed(() => isWeekendIso(todayLondon.value))
  const calendarKnown = computed(() => ukHolidays.value != null)
  const isUkHoliday = computed(() => !isWeekend.value && (ukHolidays.value?.has(todayLondon.value) ?? false))

  const hasSignal = computed(() =>
    signal.value != null && signal.value.available !== false && signal.value.decision != null)
  const signalDateLondon = computed(() =>
    hasSignal.value && signal.value!.timestamp ? londonDate(new Date(signal.value!.timestamp)) : null)
  const publishedToday = computed(() => signalDateLondon.value === todayLondon.value)
  const staleSignal = computed(() =>
    hasSignal.value && signalDateLondon.value != null && signalDateLondon.value < todayLondon.value)

  // Weekend, or a weekday the UK calendar says is a bank holiday (and no
  // decision was published today anyway).
  const noTrade = computed(() => {
    if (!isWeekend.value && !(isUkHoliday.value && !publishedToday.value)) return null
    // Holiday wording only for a real UK bank holiday (final-review M1).
    const reason = isWeekend.value ? 'Weekend — no decision' : 'UK holiday — no decision today'
    return { reason, weekend: isWeekend.value, nextTradingDay: null }
  })

  // Weekday past 09:00 London, no decision dated today, not a holiday banner:
  // 'missed' when the calendar confirms a UK trading day (alarm),
  // 'unconfirmed' when the calendar is unavailable (neutral amber).
  const lateState = computed<'missed' | 'unconfirmed' | null>(() => {
    if (noTrade.value || !staleSignal.value || nowSecs.value < MISSED_AFTER_MINUTES * 60) return null
    return calendarKnown.value ? 'missed' : 'unconfirmed'
  })

  // Weekday, before 09:00, today's decision not yet published: the verdict
  // shows the last decision's values, labelled with its date.
  const awaiting = computed(() => staleSignal.value && noTrade.value == null && lateState.value == null)

  // 2026-10-02 (paolo): a previous day's decision is named here, with its date,
  // and never fills the headline — a morning reader took 1 Oct's SELL for 2 Oct's.
  const lastDecisionLabel = computed(() =>
    (awaiting.value || lateState.value) && signalDateLondon.value
      ? `Last decision ${fmtDayMon(signalDateLondon.value)}: ${signal.value!.decision}` : null)

  // Today's decision, once published: when (London) it was decided.
  const decidedAtLabel = computed(() => {
    if (!hasSignal.value || !publishedToday.value || !signal.value!.timestamp) return null
    const ts = new Date(signal.value!.timestamp)
    return `Decided ${londonHhmm(ts)} London, ${fmtDayMon(londonDate(ts))}`
  })

  // '' once today's decision is published; 'PASSED' while a business day's
  // 08:40 decision is overdue; otherwise the time to the next UK business
  // day's 08:40 London.
  const countdown = computed(() => {
    if (publishedToday.value) return ''
    const nowMs = now.value.getTime()
    if (noTrade.value == null && pastDecisionTime.value) return 'PASSED'
    let d = todayLondon.value
    for (let i = 0; i < 14; i++, d = addDaysIso(d, 1)) {
      if (!isUkBusinessDay(d)) continue
      const target = decisionInstant(d).getTime()
      if (target > nowMs) return fmtCountdown(target - nowMs)
    }
    return ''
  })

  async function loadCalendar() {
    // Current + previous London year: a NAV-staleness walk can cross 1 Jan.
    const year = Number(todayLondon.value.slice(0, 4))
    try {
      const [prev, cur] = await Promise.all([api.calendarHolidays(undefined, year - 1), api.calendarHolidays(undefined, year)])
      ukHolidays.value = new Set([...prev, ...cur].filter(r => r.ukHoliday).map(r => r.date))
    } catch {
      ukHolidays.value = null
    }
  }

  let tickTimer: ReturnType<typeof setInterval> | null = null
  let calendarTimer: ReturnType<typeof setInterval> | null = null
  onMounted(() => {
    loadCalendar()
    tickTimer = setInterval(() => { now.value = new Date() }, TICK_MS)
    calendarTimer = setInterval(() => { if (!calendarKnown.value) loadCalendar() }, CALENDAR_RETRY_MS)
  })
  onUnmounted(() => {
    if (tickTimer) clearInterval(tickTimer)
    if (calendarTimer) clearInterval(calendarTimer)
  })

  return {
    now, todayLondon, nowSecs, pastDecisionTime, calendarKnown,
    hasSignal, signalDateLondon, publishedToday,
    noTrade, lateState, awaiting, lastDecisionLabel, decidedAtLabel, countdown,
    businessDaysBetween,
  }
}
